import { useCallback, useState, useMemo } from 'react';
import { Editor, Transforms, Range } from 'slate';
import { Persona } from '@sdk/personaCollection/types/Persona';

import { MentionPersona } from '@web/richText/types';
import getMentionSubtitle from '@web/richText/helpers/getMentionSubtitle';
import { insertMention } from '@web/richText/helpers/utils';

export type MentionsState = {
  filteredPersonas: MentionPersona[];
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  setSearch: (search: string) => void;
  onChangeValue: () => void;
  target: Range | undefined;
  index: number;
  setIndex: (index: number) => void;
  onPersonaSelect: () => void;
};

export function useMentions(editor: Editor, personas: Persona[]): MentionsState {
  const [target, setTarget] = useState<Range | undefined>();
  const [search, setSearch] = useState('');
  const [index, setIndex] = useState(0);
  const minifiedPersonas = useMemo<MentionPersona[]>(
    () => (personas ? personas.map(getMinifiedPersona) : []),
    [personas]
  );

  const filteredPersonas = useMemo(() => {
    const uppercaseSearch = search.toUpperCase();
    return minifiedPersonas
      ? minifiedPersonas.filter((p) => p.searchValue.includes(uppercaseSearch))
      : [];
  }, [minifiedPersonas, search]);

  const onPersonaSelect = useCallback(() => {
    if (filteredPersonas[index]) {
      Transforms.select(editor, target);
      insertMention(editor, filteredPersonas[index]);
      setTarget(null);
    }
  }, [editor, filteredPersonas, index, target]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (target) {
        switch (event.key) {
          case 'ArrowDown': {
            event.preventDefault();
            const prevIndex = index >= filteredPersonas.length - 1 ? 0 : index + 1;
            setIndex(prevIndex);
            break;
          }
          case 'ArrowUp': {
            event.preventDefault();
            const nextIndex = index <= 0 ? filteredPersonas.length - 1 : index - 1;
            setIndex(nextIndex);
            break;
          }
          case 'Tab':
          case 'Enter': {
            event.preventDefault();
            onPersonaSelect();
            break;
          }
          case 'Escape': {
            event.preventDefault();
            setTarget(null);
            break;
          }
        }
      }
    },
    [index, target, filteredPersonas, onPersonaSelect]
  );

  const onChangeValue = useCallback(() => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);
      const wordBefore = Editor.before(editor, start, { unit: 'word' });
      const before = wordBefore && Editor.before(editor, wordBefore);
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);
      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = afterText.match(/^(\s|$)/);

      if (beforeMatch && afterMatch) {
        setTarget(beforeRange);
        setSearch(beforeMatch[1]);
        setIndex(0);
        return;
      }
    }

    setTarget(null);
  }, [editor, setTarget, setSearch, setIndex]);

  return {
    setSearch,
    onKeyDown,
    filteredPersonas,
    onChangeValue,
    target,
    index,
    setIndex,
    onPersonaSelect,
  };
}

function getDisplayName(persona: Persona): string {
  if (persona.type === 'user') {
    return persona.fullname;
  }

  return persona.name;
}

function getMinifiedPersona(persona: Persona): MentionPersona {
  const { id, type } = persona;
  const displayName = getDisplayName(persona);
  const tempPersona: MentionPersona = {
    id,
    type,
    displayName,
    searchValue: displayName.toUpperCase(),
    mentionSubtitle: getMentionSubtitle(persona),
  };

  if (persona.type === 'user') {
    tempPersona.avatar = persona.avatar;
    tempPersona.name = persona.name;
    tempPersona.lastname = persona.lastname;
  }

  return tempPersona;
}
